export interface TutorialStep {
  id: string;
  title: string;
  description: string;
  targetElement?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: string;
  highlightElement?: string;
  route?: string;
}

export const tutorialSteps: TutorialStep[] = [
  // Step 1: Welcome & Overview
  {
    id: 'welcome',
    title: 'Welcome to Legacy',
    description: 'Let\'s walk through creating your legacy messages step by step. You can dismiss this tutorial anytime and resume later from where you left off.',
    position: 'bottom',
  },
  
  // Step 2: Choose Message Type
  {
    id: 'message-type',
    title: 'Step 1: Choose Your Message Type',
    description: 'Start by selecting how you want to communicate: write a letter, record a voice message, or create a video message. Click "New Message" to begin.',
    targetElement: '[data-tutorial="new-message-btn"]',
    position: 'left',
    highlightElement: '[data-tutorial="new-message-btn"]',
    action: 'Click "Create a Message" button',
  },
  
  {
    id: 'select-letter',
    title: 'Letter Messages',
    description: 'Choose "Letter" to write a heartfelt text message. Perfect for detailed thoughts and memories.',
    targetElement: '[data-tutorial="message-type-letter"]',
    position: 'right',
    highlightElement: '[data-tutorial="message-type-letter"]',
  },
  
  {
    id: 'select-video',
    title: 'Video Messages',
    description: 'Choose "Video" to record or upload a video message. Capture your voice, expressions, and presence.',
    targetElement: '[data-tutorial="message-type-video"]',
    position: 'right',
    highlightElement: '[data-tutorial="message-type-video"]',
  },
  
  {
    id: 'select-audio',
    title: 'Voice Messages',
    description: 'Choose "Audio" to record or upload a voice message. Share your words in your own voice.',
    targetElement: '[data-tutorial="message-type-audio"]',
    position: 'right',
    highlightElement: '[data-tutorial="message-type-audio"]',
  },
  
  // Step 3: Create Messenger
  {
    id: 'messenger-tab',
    title: 'Step 2: Add a Messenger',
    description: 'Before creating messages, you need to designate a trusted person as your messenger. They will deliver your messages when the time comes.',
    targetElement: '[data-tutorial="messengers-tab"]',
    position: 'bottom',
    highlightElement: '[data-tutorial="messengers-tab"]',
    action: 'Click "Messengers" tab',
  },
  
  {
    id: 'add-messenger',
    title: 'Invite Your Messenger',
    description: 'Click "Add Messenger" to invite someone you trust. They\'ll receive an email invitation to accept this important role.',
    targetElement: '[data-tutorial="add-messenger-btn"]',
    position: 'left',
    highlightElement: '[data-tutorial="add-messenger-btn"]',
    action: 'Click "Add Messenger" button',
  },
  
  {
    id: 'messenger-form',
    title: 'Enter Messenger Details',
    description: 'Provide your messenger\'s name and email address. They must accept the invitation before you can assign messages to them.',
    targetElement: '[data-tutorial="messenger-form"]',
    position: 'right',
  },
  
  // Step 4: Assign Messenger to Message
  {
    id: 'assign-messenger',
    title: 'Step 3: Assign Messenger to Message',
    description: 'When creating a message, select which messenger will deliver it. You can assign different messengers to different messages.',
    targetElement: '[data-tutorial="messenger-select"]',
    position: 'right',
    highlightElement: '[data-tutorial="messenger-select"]',
  },
  
  // Step 5: Write Memorial
  {
    id: 'memorial-tab',
    title: 'Step 4: Create Your Story',
    description: 'Navigate to the "My Life Story" section to write your life story and create your memorial page.',
    targetElement: '[data-tutorial="memorial-tab"]',
    position: 'bottom',
    highlightElement: '[data-tutorial="memorial-tab"]',
    action: 'Click "My Life Story" tab',
    route: '/memorial',
  },
  
  {
    id: 'memorial-text',
    title: 'Write Your Story',
    description: 'Use the text editor to write your story. Share your life story, accomplishments, and what you want people to remember.',
    targetElement: '[data-tutorial="memorial-editor"]',
    position: 'top',
    highlightElement: '[data-tutorial="memorial-editor"]',
  },
  
  // Step 6: Add Pictures
  {
    id: 'photos-tab',
    title: 'Step 5: Organize Your Photos',
    description: 'Switch to the "Photo Categories" tab to upload and organize photos from different periods of your life.',
    targetElement: '[data-tutorial="photos-tab"]',
    position: 'bottom',
    highlightElement: '[data-tutorial="photos-tab"]',
    action: 'Click "Photo Categories" tab',
  },
  
  {
    id: 'add-photos',
    title: 'Upload Memories',
    description: 'Create categories like "Childhood", "Family", "Career" and upload photos to each. These will be displayed on your memorial page.',
    targetElement: '[data-tutorial="photo-categories"]',
    position: 'top',
  },
  
  // Step 7: Add Songs
  {
    id: 'music-tab',
    title: 'Step 6: Choose My Music',
    description: 'Select the music you want played at your memorial service. Add songs that are meaningful to you.',
    targetElement: '[data-tutorial="music-tab"]',
    position: 'bottom',
    highlightElement: '[data-tutorial="music-tab"]',
    action: 'Click "My Music" tab',
  },
  
  {
    id: 'add-music',
    title: 'Select Your Songs',
    description: 'Search for songs and add them to your My Music list. You can reorder them to set the sequence.',
    targetElement: '[data-tutorial="music-selection"]',
    position: 'top',
  },
  
  // Step 8: Preview & Share
  {
    id: 'preview-memorial',
    title: 'Preview Your Story',
    description: 'Click "Preview" to see how your story page will look with all your content, photos, and music.',
    targetElement: '[data-tutorial="preview-btn"]',
    position: 'bottom',
    highlightElement: '[data-tutorial="preview-btn"]',
  },
  
  {
    id: 'share-memorial',
    title: 'Share with Messengers',
    description: 'When you\'re ready, share your story with your messengers so they can help manage your memorial page.',
    targetElement: '[data-tutorial="share-btn"]',
    position: 'bottom',
    highlightElement: '[data-tutorial="share-btn"]',
  },
  
  // Step 9: Complete
  {
    id: 'complete',
    title: 'Tutorial Complete!',
    description: 'You\'ve learned all the essential steps. You can now create messages, manage messengers, and build your legacy. Access this tutorial anytime from your dashboard.',
    position: 'bottom',
  },
];

export const getTutorialStep = (stepId: string): TutorialStep | undefined => {
  return tutorialSteps.find(step => step.id === stepId);
};

export const getNextStep = (currentStepId: string): TutorialStep | null => {
  const currentIndex = tutorialSteps.findIndex(step => step.id === currentStepId);
  if (currentIndex === -1 || currentIndex === tutorialSteps.length - 1) {
    return null;
  }
  return tutorialSteps[currentIndex + 1];
};

export const getPreviousStep = (currentStepId: string): TutorialStep | null => {
  const currentIndex = tutorialSteps.findIndex(step => step.id === currentStepId);
  if (currentIndex <= 0) {
    return null;
  }
  return tutorialSteps[currentIndex - 1];
};

export const getTutorialProgress = (currentStepId: string): number => {
  const currentIndex = tutorialSteps.findIndex(step => step.id === currentStepId);
  if (currentIndex === -1) return 0;
  return Math.round(((currentIndex + 1) / tutorialSteps.length) * 100);
};